import { Button, Col, DatePicker, Form, notification, Row, Space } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import BieuDo from '../../components/LineChart';
import { dataToOptionSelect, exportTableToExcelNew, formatNumber, newObjectKeys } from '../../utils/utility';
import { useQueries, useQuery } from '@tanstack/react-query';
import FilterTable from '../../components/FilterTable';
import {
  getDataStatisticCampaignRequest,
  getGraphicalStatisticCampaignRequest,
  getListAllBannersRequest,
  getListAllCampaignsRequest,
  getListAllPartnersRequest,
  getListAllPopupsRequest,
  getListBannerPopupTypesRequest,
  getListDimensionsRequest,
  getListMetricsRequest,
  getListPlatformsRequest,
  getListPositionsRequest,
  getListLichVietCampaignsRequest,
} from '../../services/campaignStatisticRequest';
import NormalFormLabel from '../../components/NormalFormLabel';
import CustomSelect from '../../components/CustomSelect';
import { useSelector } from 'react-redux';
import { checkPermission } from '../../utils/checkPermission';
import { GET_DATA_STATISTICS, GET_GRAPHICAL_STATISTICS } from '../../constants/apiEndPoint';
import { isEqual } from 'lodash';

const rangePresets = [
  {
    label: '7 ngày trước',
    value: [dayjs().subtract(7, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '14 ngày trước',
    value: [dayjs().subtract(14, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '30 ngày trước',
    value: [dayjs().subtract(30, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '90 ngày trước',
    value: [dayjs().subtract(90, 'days'), dayjs().subtract(1, 'days')],
  },
];

export default function ThongKeChienDich() {
  const [formSearch] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingExportData, setIsLoadingExportData] = useState(false);

  const dataRedux = useSelector((state) => state.user);
  const accountPartnerId = dataRedux?.info?.partner_id;
  const accountType = dataRedux?.info?.type;
  const checkAccountIsPartner = accountType === 2 && accountPartnerId;

  const watchCampaignIds = Form.useWatch('campaign_ids', formSearch);
  const watchLichVietCampaignIds = Form.useWatch('lich_viet_campaign_ids', formSearch);
  const watchPartnerIds = Form.useWatch('partner_ids', formSearch);
  const watchBannerPopupTypes = Form.useWatch('banner_popup_types', formSearch);
  const watchPositions = Form.useWatch('positions', formSearch);
  const [params, setParams] = useState({
    start_time: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    end_time: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
    partner_ids: [],
    campaign_ids: [],
    banner_popup_types: [],
    positions: [],
    platforms: [],
    banner_ids: [],
    popup_ids: [],
    lich_viet_campaign_ids: [],
  });

  const [filterDimensionAndMetric, setFilterDimensionAndMetric] = useState({
    pageId: 1,
    limit: 20,
    dimension_ids: checkAccountIsPartner ? [1, 3] : [1, 2, 3],
    metric_ids: [1, 2, 3, 4, 5, 6],
    order_by_conditions: [],
  });

  const [
    { data: dataAllCampaigns },
    { data: dataAllPartners },
    { data: dataBannerPopupTypes },
    { data: dataAllBanners },
    { data: dataAllPopups },
    { data: dataPositions },
    { data: dataPlatforms },
    { data: dataDimensions },
    { data: dataMetrics },
    { data: dataLichVietCampaigns },
  ] = useQueries({
    queries: [
      {
        queryKey: ['dataAllCampaignsStatistic', watchPartnerIds, watchLichVietCampaignIds],
        queryFn: () =>
          getListAllCampaignsRequest({
            partner_ids: watchPartnerIds,
            lich_viet_campaign_ids: watchLichVietCampaignIds,
          }),
      },
      {
        queryKey: ['dataAllPartnersStatistic'],
        queryFn: () => getListAllPartnersRequest(),
        enabled: !checkAccountIsPartner,
      },
      {
        queryKey: ['dataBannerPopupTypesStatistic'],
        queryFn: () => getListBannerPopupTypesRequest(),
      },
      {
        queryKey: [
          'dataAllBannersStatistic',
          watchBannerPopupTypes,
          watchCampaignIds,
          watchPositions,
          watchLichVietCampaignIds,
        ],
        queryFn: () =>
          getListAllBannersRequest({
            banner_popup_type: watchBannerPopupTypes,
            campaign_ids: watchCampaignIds,
            positions: watchPositions,
            lich_viet_campaign_ids: watchLichVietCampaignIds,
          }),
        enabled: watchBannerPopupTypes && (watchBannerPopupTypes === 3 || watchBannerPopupTypes === 4),
      },
      {
        queryKey: [
          'dataAllPopupsStatistic',
          watchBannerPopupTypes,
          watchCampaignIds,
          watchPositions,
          watchLichVietCampaignIds,
        ],
        queryFn: () =>
          getListAllPopupsRequest({
            banner_popup_type: watchBannerPopupTypes,
            campaign_ids: watchCampaignIds,
            positions: watchPositions,
            lich_viet_campaign_ids: watchLichVietCampaignIds,
          }),
        enabled: watchBannerPopupTypes && (watchBannerPopupTypes === 1 || watchBannerPopupTypes === 2),
      },
      {
        queryKey: ['dataPositionsStatistic', watchBannerPopupTypes],
        queryFn: () => getListPositionsRequest({ banner_popup_type: watchBannerPopupTypes }),
      },
      {
        queryKey: ['dataPlatformsStatistic'],
        queryFn: () => getListPlatformsRequest(),
      },
      {
        queryKey: ['dataDimensionsStatistic'],
        queryFn: () => getListDimensionsRequest(),
      },
      {
        queryKey: ['dataMetricsStatistic'],
        queryFn: () => getListMetricsRequest(),
      },
      {
        queryKey: ['dataLichVietCampaigns', watchCampaignIds],
        queryFn: () => getListLichVietCampaignsRequest({ campaign_ids: watchCampaignIds }),
      },
    ],
  });

  const {
    data: dataStatisticCampaigns,
    refetch: refetchDataStatisticCampaigns,
    isPending: isPendingDataStatisticCampaigns,
  } = useQuery({
    queryKey: ['dataStatisticCampaigns', { ...params, ...filterDimensionAndMetric }],
    queryFn: () =>
      getDataStatisticCampaignRequest({
        ...params,
        ...filterDimensionAndMetric,
        page: filterDimensionAndMetric?.pageId,
        pageId: undefined,
      }),
  });

  useEffect(() => {
    formSearch.setFieldValue('positions', []);
    formSearch.setFieldValue('banner_ids', []);
    formSearch.setFieldValue('popup_ids', []);
  }, [watchBannerPopupTypes, formSearch]);

  useEffect(() => {
    formSearch.setFieldValue('banner_ids', []);
    formSearch.setFieldValue('popup_ids', []);
  }, [watchPositions, formSearch]);

  const handleSubmitForm = (values) => {
    const temp = {};

    if (values?.timeRange) {
      Object.assign(temp, {
        start_time: dayjs(values?.timeRange?.[0]).format('YYYY-MM-DD'),
        end_time: dayjs(values?.timeRange?.[1]).format('YYYY-MM-DD'),
      });
    }

    if (values?.campaign_ids) {
      Object.assign(temp, { campaign_ids: values?.campaign_ids });
    } else {
      Object.assign(temp, { campaign_ids: [] });
    }

    if (values?.partner_ids) {
      Object.assign(temp, { partner_ids: values?.partner_ids });
    } else {
      Object.assign(temp, { partner_ids: [] });
    }

    if (values?.banner_popup_types) {
      Object.assign(temp, { banner_popup_types: [values?.banner_popup_types] });
    } else {
      Object.assign(temp, { banner_popup_types: [] });
    }

    if (values?.banner_ids) {
      Object.assign(temp, { banner_ids: values?.banner_ids });
    } else {
      Object.assign(temp, { banner_ids: [] });
    }

    if (values?.positions) {
      Object.assign(temp, { positions: values?.positions });
    } else {
      Object.assign(temp, { positions: [] });
    }

    if (values?.popup_ids) {
      Object.assign(temp, { popup_ids: values?.popup_ids });
    } else {
      Object.assign(temp, { popup_ids: [] });
    }

    if (values?.platforms) {
      Object.assign(temp, { platforms: [values?.platforms] });
    } else {
      Object.assign(temp, { platforms: [] });
    }

    if (values?.lich_viet_campaign_ids) {
      Object.assign(temp, { lich_viet_campaign_ids: values?.lich_viet_campaign_ids });
    } else {
      Object.assign(temp, { lich_viet_campaign_ids: [] });
    }

    if (isEqual(temp, params)) {
      refetchDataStatisticCampaigns();
    }
    setParams(temp);
    setFilterDimensionAndMetric({ ...filterDimensionAndMetric, pageId: 1, limit: 20 });
  };

  const handleExportData = async () => {
    setIsLoadingExportData(true);
    const dataExport = await getDataStatisticCampaignRequest({
      ...params,
      ...filterDimensionAndMetric,
      page: 1,
      pageId: undefined,
      limit: undefined,
    });

    if (!dataExport?.data?.statistics || dataExport?.data?.statistics.length === 0) {
      notification.error({
        message: 'Xuất file dữ liệu thất bại. Vui lòng thử lại sau!',
      });
      setIsLoadingExportData(false);
      return;
    }

    let dataExportTmp = [];

    dataExport?.data?.statistics?.forEach((statistic, i) => {
      const value = statistic.reduce((acc, item) => {
        acc[item.name] = formatNumber(item.value);
        acc.type = item.type;
        acc.id = item.id;
        acc.key = i;
        return acc;
      }, {});
      dataExportTmp.push(value);
    });

    const fixedColumns = dataExport?.data?.total || [];
    const colSpan = dataExport?.data?.statistics[0].filter((item) => item.type === 'dimension')?.length || 0;

    const dataExportFinal = {
      data: dataExportTmp,
      fixedColumns: fixedColumns,
      colSpan: colSpan,
    };

    exportTableToExcelNew(
      dataExportFinal,
      `Thongkechiendich_tungay_${dayjs(params?.start_time).format('DD-MM-YYYY')}_denngay_${dayjs(
        params?.end_time
      ).format('DD-MM-YYYY')}`
    );
    setIsLoadingExportData(false);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Thống kê chiến dịch</h1>
        <div className="flex justify-end gap-2">
          <Button loading={isLoadingExportData} type="primary" onClick={() => handleExportData()}>
            <DownloadOutlined /> Export
          </Button>

          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            <PlusOutlined /> Thêm biểu đồ
          </Button>
        </div>
      </div>

      <Form layout="vertical" name="search-campaigns" form={formSearch} onFinish={handleSubmitForm}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={6}>
            <Form.Item
              label={<NormalFormLabel text="Thời gian" />}
              name="timeRange"
              initialValue={[dayjs(params?.start_time, 'YYYY-MM-DD'), dayjs(params?.end_time, 'YYYY-MM-DD')]}
            >
              <DatePicker.RangePicker
                placeholder={['Từ ngày', 'Đến ngày']}
                format="DD-MM-YYYY"
                presets={rangePresets}
                allowClear={false}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Chiến dịch quảng cáo" />} name="campaign_ids">
              <CustomSelect
                placeholder="Chiến dịch quảng cáo"
                options={dataToOptionSelect(dataAllCampaigns || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Chiến dịch ghi nhận order" />} name="lich_viet_campaign_ids">
              <CustomSelect
                placeholder="Chiến dịch ghi nhận order"
                options={dataToOptionSelect(dataLichVietCampaigns || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          {!checkAccountIsPartner && (
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<NormalFormLabel text="Đối tác" />} name="partner_ids">
                <CustomSelect
                  placeholder="Đối tác"
                  options={dataToOptionSelect(dataAllPartners || [])}
                  mode="multiple"
                />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Loại banner/ popup" />} name="banner_popup_types">
              <CustomSelect
                placeholder="Loại banner/ popup"
                options={dataToOptionSelect(dataBannerPopupTypes || [], 'name', 'value')}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Vị trí hiển thị" />} name="positions">
              <CustomSelect
                placeholder="Chọn vị trí hiển thị"
                mode="multiple"
                options={dataToOptionSelect(dataPositions || [], 'name', 'value')}
              />
            </Form.Item>
          </Col>

          {watchBannerPopupTypes && (watchBannerPopupTypes === 3 || watchBannerPopupTypes === 4) && (
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<NormalFormLabel text="Banner" />} name="banner_ids">
                <CustomSelect placeholder="Banner" options={dataToOptionSelect(dataAllBanners || [])} mode="multiple" />
              </Form.Item>
            </Col>
          )}

          {watchBannerPopupTypes && (watchBannerPopupTypes === 1 || watchBannerPopupTypes === 2) && (
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<NormalFormLabel text="Popup" />} name="popup_ids">
                <CustomSelect placeholder="Popup" options={dataToOptionSelect(dataAllPopups || [])} mode="multiple" />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Nền tảng" />} name="platforms">
              <CustomSelect placeholder="Nền tảng" options={dataToOptionSelect(dataPlatforms || [], 'name', 'value')} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item>
              <Space direction="horizontal" className="sm:mt-7">
                <Button type="primary" htmlType="submit">
                  <SearchOutlined /> Tìm kiếm
                </Button>
                {params &&
                  Object.values(
                    newObjectKeys(params, [
                      'start_time',
                      'end_time',
                      'campaign_ids',
                      'partner_ids',
                      'banner_popup_types',
                      'banner_ids',
                      'popup_ids',
                      'positions',
                      'platforms',
                      'lich_viet_campaign_ids',
                    ])
                  ).some((value) => (value && !Array.isArray(value)) || value?.length > 0) && (
                    <Button
                      htmlType="button"
                      onClick={() => {
                        formSearch.resetFields();
                        formSearch.setFieldValue('timeRange', [
                          dayjs().subtract(30, 'days'),
                          dayjs().subtract(1, 'days'),
                        ]);
                        setParams({
                          start_time: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
                          end_time: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
                          partner_ids: [],
                          campaign_ids: [],
                          banner_popup_types: [],
                          positions: [],
                          platforms: [],
                          banner_ids: [],
                          popup_ids: [],
                        });
                        setFilterDimensionAndMetric({ ...filterDimensionAndMetric, pageId: 1, limit: 20 });
                      }}
                    >
                      <DeleteOutlined />
                      Xóa lọc
                    </Button>
                  )}
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {checkPermission(GET_GRAPHICAL_STATISTICS, 'Xem biểu đồ thống kê') && (
        <BieuDo
          keyChart="campaign"
          whereCondition={params}
          apiCallBack={getGraphicalStatisticCampaignRequest}
          dataDimensions={dataDimensions}
          dataMetrics={dataMetrics}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      )}

      {checkPermission(GET_DATA_STATISTICS, 'Xem dữ liệu thống kê') && (
        <div className="flex w-full">
          <FilterTable
            tableKey="campaign"
            isLoadingFilterTable={isPendingDataStatisticCampaigns}
            filterDimensionAndMetric={filterDimensionAndMetric}
            setFilterDimensionAndMetric={setFilterDimensionAndMetric}
            dataDimensions={checkAccountIsPartner ? dataDimensions?.filter((item) => item.id !== 2) : dataDimensions}
            dataMetrics={dataMetrics}
            dataStatisticCampaigns={dataStatisticCampaigns}
          />
        </div>
      )}
    </div>
  );
}
